import { createContext, type ReactNode, useContext, useEffect } from 'react';
import { LoginModal } from '../components/auth/LoginModal';
import { useCurrentUser, useLogout } from './api/useAuth';
import { useLoginModal } from './useLoginModal';

interface User {
  id: string;
  email: string;
  name: string;
  roles: string[];
  firmId: string;
  active: boolean;
  lastLoginAt?: string;
  hasActiveSubscription?: boolean;
  createdAt?: string;
}

interface Firm {
  id: string;
  name: string;
  size: string;
  maxAttorneys: number;
  claudeSeats: number;
}

interface License {
  tier: string;
  validUntil: string;
  seats: number;
  claudeSeats: number;
  dailyTokenLimit: number;
  features: string[];
}

interface AuthContextType {
  user: User | null;
  firm: Firm | null;
  license: License | null;
  availableServices: Array<{
    id: string;
    name: string;
    provider: string;
    type: string;
  }>;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => void;
  hasRole: (role: 'attorney' | 'admin' | 'superadmin') => boolean;
  hasAnyRole: (roles: ('attorney' | 'admin' | 'superadmin')[]) => boolean;
  requireAuth: (
    action: () => void,
    options?: {
      redirectOnCancel?: string;
      title?: string;
      description?: string;
    },
  ) => void;
  openLogin: (options?: {
    redirectOnCancel?: string;
    title?: string;
    description?: string;
    force?: boolean;
  }) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: userProfile, isLoading, error } = useCurrentUser();
  const logoutMutation = useLogout();
  const { isOpen, forceOpen, redirectOnCancel, title, description, openLogin, closeLogin } =
    useLoginModal();

  useEffect(() => {
    // Clear invalid auth tokens on error
    if (!isLoading && error) {
      localStorage.removeItem('sessionToken');
      localStorage.removeItem('auth_nonce');
    }
  }, [isLoading, error]);

  const logout = () => {
    logoutMutation.mutate();
  };

  const hasRole = (role: 'attorney' | 'admin' | 'superadmin'): boolean => {
    if (!userProfile?.user) return false;

    if (role === 'attorney') {
      // Any authenticated user has attorney access
      return true;
    }

    if (role === 'admin') {
      // Admin or superadmin has admin access
      return (
        userProfile.user.roles.includes('admin') || userProfile.user.roles.includes('superadmin')
      );
    }

    if (role === 'superadmin') {
      // Only superadmin has superadmin access
      return userProfile.user.roles.includes('superadmin');
    }

    return false;
  };

  const hasAnyRole = (roles: ('attorney' | 'admin' | 'superadmin')[]): boolean => {
    return roles.some((role) => hasRole(role));
  };

  // Helper to require authentication before performing an action
  // If user is authenticated, runs the action immediately
  // If not authenticated, opens login modal with specified options
  const requireAuth = (
    action: () => void,
    options?: {
      redirectOnCancel?: string;
      title?: string;
      description?: string;
    },
  ) => {
    if (userProfile?.user) {
      action();
    } else {
      openLogin(options);
    }
  };

  const value: AuthContextType = {
    user: userProfile?.user || null,
    firm: userProfile?.firm || null,
    license: userProfile?.license || null,
    availableServices: userProfile?.availableServices || [],
    isAuthenticated: !!userProfile?.user,
    isLoading,
    logout,
    hasRole,
    hasAnyRole,
    requireAuth,
    openLogin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}

      {/* Global login modal */}
      <LoginModal
        isOpen={isOpen && (forceOpen || !userProfile?.user)}
        onClose={closeLogin}
        redirectOnCancel={redirectOnCancel}
        title={title}
        description={description}
      />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
